import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminService from '../services/SuperAdminService';
import './SuperAdminDashboard.css';

function SuperAdminDashboard({ onLogout }) {
  const [stats, setStats] = useState({
    totalInstitutions: 0,
    totalColleges: 0,
    totalSchools: 0,
    totalActive: 0,
    totalInactive: 0
  });
  const [institutions, setInstitutions] = useState([]);
  const [filteredInstitutions, setFilteredInstitutions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [user, setUser] = useState(null);

  // Modals state
  const [showRegModal, setShowRegModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageTargetOrg, setStorageTargetOrg] = useState(null);
  const [quotaGbInput, setQuotaGbInput] = useState(50);
  const [retentionInput, setRetentionInput] = useState(365);
  
  // Registration / Edit Form States
  const [formData, setFormData] = useState({
    orgId: '',
    orgName: '',
    password: '',
    email: '',
    phoneNumber: '',
    institutionType: 'DEGREE_COLLEGE',
    city: '',
    state: '',
    pincode: '',
    address: '',
    principalName: '',
    affiliation: '',
    isMultiLevel: false,
    storageQuotaGb: 50
  });
  
  const [editingOrgId, setEditingOrgId] = useState('');

  const openStorageModal = (inst) => {
    setStorageTargetOrg(inst);
    setQuotaGbInput(inst.storageQuotaGb || 50);
    setRetentionInput(inst.retentionDays || 365);
    setShowStorageModal(true);
  };

  const handleStorageQuotaSubmit = async (e) => {
    e.preventDefault();
    if (!storageTargetOrg) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await SuperAdminService.updateStorageLimit(
        storageTargetOrg.orgId,
        Number(quotaGbInput),
        Number(retentionInput)
      );
      if (res && res.success) {
        triggerSuccess(`Storage quota updated to ${quotaGbInput} GB for '${storageTargetOrg.orgName}'!`);
        setShowStorageModal(false);
        fetchData();
      } else {
        triggerError(res.error || 'Failed to update storage quota.');
      }
    } catch (err) {
      triggerError(err.message || 'Connection error while updating storage quota.');
    } finally {
      setSubmitting(false);
    }
  };

  const navigate = useNavigate();

  // Load User Data & Fetch API Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const statsRes = await SuperAdminService.getStats();
      if (statsRes && statsRes.success) {
        setStats(statsRes);
      }

      const listRes = await SuperAdminService.getInstitutions();
      if (listRes && listRes.success) {
        setInstitutions(listRes.institutions || []);
        setFilteredInstitutions(listRes.institutions || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load system data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const adminUser = localStorage.getItem('superAdminUser');
    if (!adminUser) {
      navigate('/superadmin/login');
      return;
    }
    setUser(JSON.parse(adminUser));
    fetchData();
  }, [navigate, fetchData]);

  // Handle Search and Filter
  useEffect(() => {
    let result = [...institutions];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(inst => 
        inst.orgName?.toLowerCase().includes(query) ||
        inst.orgId?.toLowerCase().includes(query) ||
        inst.city?.toLowerCase().includes(query) ||
        inst.principalName?.toLowerCase().includes(query)
      );
    }

    if (typeFilter !== 'ALL') {
      if (typeFilter === 'SCHOOL') {
        result = result.filter(inst => inst.institutionType === 'SCHOOL');
      } else if (typeFilter === 'COLLEGE') {
        result = result.filter(inst => inst.institutionType !== 'SCHOOL');
      }
    }

    setFilteredInstitutions(result);
  }, [searchQuery, typeFilter, institutions]);

  // Toast / Messages helper
  const triggerSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const triggerError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 5000);
  };

  // Log out
  const handleSignOut = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdminUser');
    onLogout();
    navigate('/superadmin/login');
  };

  // Open Registration Modal
  const openRegistration = () => {
    setFormData({
      orgId: '',
      orgName: '',
      password: '',
      email: '',
      phoneNumber: '',
      institutionType: 'DEGREE_COLLEGE',
      city: '',
      state: '',
      pincode: '',
      address: '',
      principalName: '',
      affiliation: '',
      isMultiLevel: false
    });
    setError('');
    setShowRegModal(true);
  };

  // Register Submit
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await SuperAdminService.registerInstitution(formData);
      if (res && res.success) {
        triggerSuccess(`Institution '${formData.orgName}' successfully registered!`);
        setShowRegModal(false);
        fetchData();
      } else {
        triggerError(res.error || 'Failed to register institution.');
      }
    } catch (err) {
      triggerError(err.message || 'Connection error while registering.');
    } finally {
      setSubmitting(false);
    }
  };

  // Activate / Deactivate
  const handleToggleActive = async (orgId, currentStatus) => {
    try {
      let res;
      if (currentStatus) {
        res = await SuperAdminService.deactivateInstitution(orgId);
      } else {
        res = await SuperAdminService.activateInstitution(orgId);
      }
      
      if (res && res.success) {
        triggerSuccess(`Institution status updated for org ID: ${orgId}`);
        fetchData();
      } else {
        triggerError(res.error || 'Failed to update status.');
      }
    } catch (err) {
      triggerError(err.message || 'Connection error.');
    }
  };

  // Open Edit Modal
  const openEdit = (inst) => {
    setEditingOrgId(inst.orgId);
    setFormData({
      orgId: inst.orgId,
      orgName: inst.orgName,
      email: inst.email,
      password: '',  // blank by default — only set if admin wants to reset
      phoneNumber: inst.phoneNumber || '',
      institutionType: inst.institutionType || 'DEGREE_COLLEGE',
      city: inst.city || '',
      state: inst.state || '',
      pincode: inst.pincode || '',
      address: inst.address || '',
      principalName: inst.principalName || '',
      affiliation: inst.affiliation || '',
      isMultiLevel: inst.isMultiLevel || false
    });
    setError('');
    setShowEditModal(true);
  };

  // Edit Submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await SuperAdminService.updateInstitution(editingOrgId, formData);
      if (res && res.success) {
        triggerSuccess(`Institution '${formData.orgName}' successfully updated!`);
        setShowEditModal(false);
        fetchData();
      } else {
        triggerError(res.error || 'Failed to update institution.');
      }
    } catch (err) {
      triggerError(err.message || 'Connection error while updating.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="superadmin-dashboard-container">
      {/* ── TOP NAVIGATION ── */}
      <header className="superadmin-dash-header">
        <div className="dash-brand">
          <div className="logo-badge">S²G</div>
          <div>
            <h1>Super Admin Operations</h1>
            <p className="subtext">Centralized Platform Control Console</p>
          </div>
        </div>
        <div className="dash-user-controls">
          <div className="user-info">
            <span className="user-icon">👤</span>
            <div>
              <span className="username">{user?.username}</span>
              <span className="user-role">System Root</span>
            </div>
          </div>
          <button className="btn-logout" onClick={handleSignOut}>
            Sign Out
          </button>
        </div>
      </header>

      {/* ── ALERTS / TOASTS ── */}
      {successMsg && (
        <div className="dash-toast toast-success">
          <span className="icon">✓</span> {successMsg}
        </div>
      )}
      {error && (
        <div className="dash-toast toast-error">
          <span className="icon">⚠️</span> {error}
        </div>
      )}

      {/* ── STATS CARDS GRID ── */}
      <section className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon purple">🏢</div>
          <div className="stat-content">
            <h3>Total Managed</h3>
            <div className="value">{stats.totalInstitutions}</div>
            <p>Active institutions onboarded</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon indigo">🎓</div>
          <div className="stat-content">
            <h3>Colleges</h3>
            <div className="value">{stats.totalColleges}</div>
            <p>Higher education centers</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue">🏫</div>
          <div className="stat-content">
            <h3>Schools</h3>
            <div className="value">{stats.totalSchools}</div>
            <p>Primary/Secondary academies</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">●</div>
          <div className="stat-content">
            <h3>Active Status</h3>
            <div className="value green-text">{stats.totalActive}</div>
            <p>Operational org systems</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">●</div>
          <div className="stat-content">
            <h3>Disabled</h3>
            <div className="value red-text">{stats.totalInactive}</div>
            <p>System offline or suspended</p>
          </div>
        </div>
      </section>

      {/* ── MAIN WORKSPACE ── */}
      <main className="dashboard-main-content">
        <div className="table-controls-bar">
          <div className="search-filter-wrapper">
            <input 
              type="text" 
              placeholder="Search by name, ID, city..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <select 
              value={typeFilter} 
              onChange={(e) => setTypeFilter(e.target.value)}
              className="type-dropdown"
            >
              <option value="ALL">All Types</option>
              <option value="COLLEGE">Colleges Only</option>
              <option value="SCHOOL">Schools Only</option>
            </select>
          </div>
          <button className="btn-add-inst" onClick={openRegistration}>
            <span className="plus-sign">+</span> Register Institution
          </button>
        </div>

        {/* ── INSTITUTIONS TABLE ── */}
        <div className="table-card">
          {loading ? (
            <div className="dashboard-loading">
              <div className="spinner"></div>
              <p>Fetching platform nodes...</p>
            </div>
          ) : filteredInstitutions.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📁</span>
              <h3>No Institutions Found</h3>
              <p>Try refining your search terms or register a new one to begin.</p>
            </div>
          ) : (
            <div className="responsive-table-wrapper">
              <table className="super-table">
                <thead>
                  <tr>
                    <th>Org ID</th>
                    <th>Institution Name</th>
                    <th>Type</th>
                    <th>Principal</th>
                    <th>Contact Info</th>
                    <th>Location</th>
                    <th>Storage Quota</th>
                    <th>Status</th>
                    <th className="text-right">Management</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstitutions.map((inst) => (
                    <tr key={inst.orgId} className={inst.active ? '' : 'inactive-row'}>
                      <td className="org-id-cell">
                        <code>{inst.orgId}</code>
                      </td>
                      <td className="org-name-cell">
                        <div className="primary-name">{inst.orgName}</div>
                        {inst.affiliation && <div className="sub-affiliation">{inst.affiliation}</div>}
                      </td>
                      <td>
                        <span className={`badge-type ${inst.institutionType?.toLowerCase()}`}>
                          {inst.institutionType}
                        </span>
                      </td>
                      <td>{inst.principalName || <span className="placeholder-text">—</span>}</td>
                      <td>
                        <div className="email-link">{inst.email}</div>
                        <div className="phone-sub">{inst.phoneNumber}</div>
                      </td>
                      <td>
                        {inst.city ? `${inst.city}, ${inst.state}` : <span className="placeholder-text">—</span>}
                      </td>
                      <td>
                        <button
                          onClick={() => openStorageModal(inst)}
                          title="Click to Configure GB Storage Limit"
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <span style={{ fontSize: '14px' }}>💾</span>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>
                              {inst.storageQuotaGb ? `${inst.storageQuotaGb} GB` : '50.0 GB'}
                            </div>
                            <div style={{ fontSize: '10px', color: '#64748b' }}>
                              Used: {inst.storageUsedMb || '0.00 MB'}
                            </div>
                          </div>
                        </button>
                      </td>
                      <td>
                        <span className={`status-pill ${inst.active ? 'active' : 'inactive'}`}>
                          {inst.active ? 'Active' : 'Deactivated'}
                        </span>
                      </td>
                      <td className="actions-cell text-right">
                        <button 
                          className="btn-action edit" 
                          onClick={() => openStorageModal(inst)}
                          title="Configure Storage Limit"
                          style={{ marginRight: '4px' }}
                        >
                          💾
                        </button>
                        <button 
                          className="btn-action edit" 
                          onClick={() => openEdit(inst)}
                          title="Edit Details"
                        >
                          ✏️
                        </button>
                        <button 
                          className={`btn-action toggle-status ${inst.active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleActive(inst.orgId, inst.active)}
                          title={inst.active ? 'Deactivate Institution' : 'Activate Institution'}
                        >
                          {inst.active ? '🔒' : '🔓'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* ── REGISTRATION MODAL ── */}
      {showRegModal && (
        <div className="super-modal-backdrop">
          <div className="super-modal-box">
            <div className="modal-header">
              <h2>Register New Node</h2>
              <button className="btn-close-modal" onClick={() => setShowRegModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleRegisterSubmit} className="modal-form">
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Organization Unique ID (orgId)*</label>
                  <input 
                    type="text" 
                    value={formData.orgId}
                    onChange={(e) => setFormData({...formData, orgId: e.target.value.trim()})}
                    placeholder="e.g. ssquareg1" 
                    required 
                  />
                </div>
                <div className="form-field">
                  <label>Institution/Org Name*</label>
                  <input 
                    type="text" 
                    value={formData.orgName}
                    onChange={(e) => setFormData({...formData, orgName: e.target.value})}
                    placeholder="e.g. S Square G College" 
                    required 
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Primary Admin Email*</label>
                  <input 
                    type="email" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="e.g. admin@ssquareg.com" 
                    required 
                  />
                </div>
                <div className="form-field">
                  <label>Admin Contact Phone</label>
                  <input 
                    type="text" 
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                    placeholder="e.g. 9876543210" 
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Admin Password*</label>
                <input 
                  type="password" 
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Set login password for college/school admin" 
                  required 
                />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Institution Class Type*</label>
                  <select 
                    value={formData.institutionType}
                    onChange={(e) => setFormData({...formData, institutionType: e.target.value})}
                  >
                    <option value="SCHOOL">SCHOOL</option>
                    <option value="PUC_COLLEGE">PUC_COLLEGE</option>
                    <option value="DEGREE_COLLEGE">DEGREE_COLLEGE</option>
                    <option value="MULTI_SUPPORT">MULTI_SUPPORT</option>
                  </select>
                </div>
                <div className="form-field checkbox-field">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={formData.isMultiLevel}
                      onChange={(e) => setFormData({...formData, isMultiLevel: e.target.checked})}
                    />
                    Enable Multi-Level Operations
                  </label>
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-field">
                  <label>City</label>
                  <input 
                    type="text" 
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <input 
                    type="text" 
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input 
                    type="text" 
                    value={formData.pincode}
                    onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Physical Address</label>
                <textarea 
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  rows="2"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Principal/Director Name</label>
                  <input 
                    type="text" 
                    value={formData.principalName}
                    onChange={(e) => setFormData({...formData, principalName: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Affiliation Details</label>
                  <input 
                    type="text" 
                    value={formData.affiliation}
                    onChange={(e) => setFormData({...formData, affiliation: e.target.value})}
                    placeholder="e.g. CBSE / State Board / University"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowRegModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating Secure Node...' : 'Register Secure Node'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {showEditModal && (
        <div className="super-modal-backdrop">
          <div className="super-modal-box">
            <div className="modal-header">
              <h2>Edit Node Details (Org: {editingOrgId})</h2>
              <button className="btn-close-modal" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="modal-form">
              <div className="form-field">
                <label>Institution/Org Name*</label>
                <input 
                  type="text" 
                  value={formData.orgName}
                  onChange={(e) => setFormData({...formData, orgName: e.target.value})}
                  required 
                />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Primary Admin Email*</label>
                  <input 
                    type="email" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required 
                  />
                </div>
                <div className="form-field">
                  <label>Admin Contact Phone</label>
                  <input 
                    type="text" 
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Reset Admin Password <span style={{color:'#64748b', fontWeight:'400', textTransform:'none'}}>(leave blank to keep current password)</span></label>
                <input 
                  type="password" 
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Enter new password to reset, or leave blank" 
                />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Institution Class Type*</label>
                  <select 
                    value={formData.institutionType}
                    onChange={(e) => setFormData({...formData, institutionType: e.target.value})}
                  >
                    <option value="SCHOOL">SCHOOL</option>
                    <option value="PUC_COLLEGE">PUC_COLLEGE</option>
                    <option value="DEGREE_COLLEGE">DEGREE_COLLEGE</option>
                    <option value="MULTI_SUPPORT">MULTI_SUPPORT</option>
                  </select>
                </div>
                <div className="form-field checkbox-field">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      checked={formData.isMultiLevel}
                      onChange={(e) => setFormData({...formData, isMultiLevel: e.target.checked})}
                    />
                    Enable Multi-Level Operations
                  </label>
                </div>
              </div>

              <div className="form-grid-3">
                <div className="form-field">
                  <label>City</label>
                  <input 
                    type="text" 
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>State</label>
                  <input 
                    type="text" 
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input 
                    type="text" 
                    value={formData.pincode}
                    onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Physical Address</label>
                <textarea 
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  rows="2"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Principal/Director Name</label>
                  <input 
                    type="text" 
                    value={formData.principalName}
                    onChange={(e) => setFormData({...formData, principalName: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Affiliation Details</label>
                  <input 
                    type="text" 
                    value={formData.affiliation}
                    onChange={(e) => setFormData({...formData, affiliation: e.target.value})}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving Changes...' : 'Save Institution Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STORAGE QUOTA LIMIT MODAL ── */}
      {showStorageModal && storageTargetOrg && (
        <div className="super-modal-backdrop">
          <div className="super-modal-box" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>💾 Configure Storage Quota Limit</h2>
              <button className="btn-close-modal" onClick={() => setShowStorageModal(false)}>×</button>
            </div>

            <form onSubmit={handleStorageQuotaSubmit} className="modal-form">
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>{storageTargetOrg.orgName}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Org ID: <code>{storageTargetOrg.orgId}</code> • Current Usage: <b>{storageTargetOrg.storageUsedMb || '0.00 MB'}</b>
                </div>
              </div>

              {/* Preset Buttons */}
              <div className="form-field">
                <label>Storage Limit Presets (GB)</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px', marginBottom: '12px' }}>
                  {[50, 100, 250, 500, 1000].map(gb => (
                    <button
                      key={gb}
                      type="button"
                      onClick={() => setQuotaGbInput(gb)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        border: '1px solid',
                        borderColor: Number(quotaGbInput) === gb ? '#2563eb' : '#cbd5e1',
                        backgroundColor: Number(quotaGbInput) === gb ? '#eff6ff' : '#ffffff',
                        color: Number(quotaGbInput) === gb ? '#2563eb' : '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      {gb >= 1000 ? `${gb / 1000} TB (${gb} GB)` : `${gb} GB`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Storage Limit (GB)*</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={quotaGbInput}
                    onChange={(e) => setQuotaGbInput(e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Auto-Retention Period (Days)*</label>
                  <select
                    value={retentionInput}
                    onChange={(e) => setRetentionInput(e.target.value)}
                  >
                    <option value={180}>180 Days (6 Months)</option>
                    <option value={365}>365 Days (1 Year)</option>
                    <option value={730}>730 Days (2 Years)</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions" style={{ marginTop: '20px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowStorageModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving Quota...' : 'Save Storage Quota'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
